import type { CaseDocumentType } from '@/types/case.types';
import type { UserResponse, WorkOrderAttempt } from '@/types/workOrderAttempt.types';
import type { UserResponseSchema } from '@/types/user.types';
import { ServiceResponse } from './service.types';
import { VendorContact, VendorResponse } from './vendor.types';
import type { InvoiceResponse } from '@/types/invoice.types';
import type { EmailLogResponse } from '@/types/types';
import { Team } from './team.types';

// Work Order Document Types
export interface WorkOrderDocument {
  caseDocTypeId: string;
  workOrderDocId?: string | null;
  /** ZOHO-1467: name for this work order only; null means the master name is used. */
  editedName?: string | null;
  caseDocType?: CaseDocumentType | null;
  createdAt?: Date | null;
  updatedAt?: Date | null;
  deletedAt?: Date | null;
}

export interface WorkOrderDocumentPatch {
  create?:
    | {
        caseDocTypeId: string;
        workOrderDocId?: string | null;
        editedName?: string | null;
        caseDocType?: CaseDocumentType | null;
        createdAt?: Date | null;
        updatedAt?: Date | null;
        deletedAt?: Date | null;
      }[]
    | null;
  update?:
    | {
        workOrderDocId: string;
        editedName: string | null;
      }[]
    | null;
  delete?:
    | {
        workOrderDocId?: string;
      }[]
    | null;
}
// Private Info Types
export interface PrivateInfo {
  privateInfoId?: string;
  ssn?: string;
  dateOfBirth?: Date;
  dlNumber?: string;
  others?: string;
  workOrderId?: string;
}

// Work Order Base Types
export interface WorkOrder {
  workOrderId?: string;
  number?: string;
  type: 'INDIVIDUAL' | 'BUSINESS';
  toServe: string;
  address1: string;
  address2?: string | null;
  addressType: 'BUSINESS' | 'HOME';
  phone?: string | null;
  city: string;
  state: string;
  zipCode: string;
  country?: string | null;
  affidavitTypeId: string;
  serverId?: string | null;
  serverInstructions?: string | null;
  service?: ServiceResponse | null;
  teamId?: string;
  team?: Team;
  userId?: string;
  user?: UserResponse;
  isPrepaid?: boolean;
  /** Linked Home Inspection work order id when created as a PS add-on. */
  linkedHIWorkOrderID?: string | null;
}

// Work Order Create Request
export interface WorkOrderCreateRequest extends WorkOrder {
  privateInfo?: PrivateInfo | null;
  documents: WorkOrderDocument[];
}

// Work Order Update Request
export interface WorkOrderUpdateRequest extends WorkOrder {
  privateInfo?: PrivateInfo | null;
  documents: WorkOrderDocument[];
  status: ProcessStatus;
}

export interface WorkOrderPatchRequest extends Partial<WorkOrder> {
  status?: ProcessStatus;
}

// Work Order Patch Update Request
export interface WorkOrderUpdatePatchRequest {
  workOrderId?: string;
  type?: 'INDIVIDUAL' | 'BUSINESS';
  toServe?: string;
  address1?: string;
  address2?: string | null;
  addressType?: 'BUSINESS' | 'HOME';
  phone?: string | null;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string | null;
  affidavitTypeId?: string;
  serverId?: string | null;
  serverInstructions?: string | null;
  privateInfo?: PrivateInfo | null;
  documents?: WorkOrderDocumentPatch | null;
  status?: ProcessStatus;
}

export enum Action {
  SERVE = 'SERVE',
  POST = 'POST',
  ISSUE_THEN_SERVE = 'ISSUE_THEN_SERVE',
  OTHER = 'OTHER',
}

export enum AfterAction {
  NOTARIZE_AFFIDAVIT = 'NOTARIZE_AFFIDAVIT',
  FILE_AFFIDAVIT = 'FILE_AFFIDAVIT',
  NEED_ORIGINAL_AFFIDAVIT = 'NEED_ORIGINAL_AFFIDAVIT',
  USE_PROOF_SERVICE = 'USE_PROOF_SERVICE',
  OTHER = 'OTHER',
}

export enum DepartmentType {
  DEPUTIZATION = 'DEPUTIZATION',
  NATIONAL = 'NATIONAL',
  INTERNATIONAL = 'INTERNATIONAL',
  UIDDA = 'UIDDA',
  DUE_DILIGENCE = 'DUE_DILIGENCE',
}

export enum PaymentType {
  WITNESS_FEE = 'WITNESS_FEE',
  OPERATING_CHECK = 'OPERATING_CHECK',
}

export interface WorkOrderVendor {
  vendorId?: string;
  department?: DepartmentType;
  vendorContactId?: string;
  action?: Action;
  otherAction?: string | null;
  afterAction?: AfterAction[];
  otherAfterAction?: string | null;
  amount?: number;
  trackingNumber?: string;
  vendor?: VendorResponse;
  vendorContact?: VendorContact;
}

export enum WorkOrderPaymentStatus {
  REQUESTED = 'REQUESTED',
  BILLED = 'BILLED',
  VOID = 'VOID',
  CASHED = 'CASHED',
}
export interface WorkOrderPayment {
  paymentId?: string;
  issueDate?: string;
  issuedTo?: string;
  amount?: number;
  reference?: string;
  checkNumber?: number;
  paymentType?: PaymentType;
  status?: WorkOrderPaymentStatus;
}

// Work Order Response Types
export interface WorkOrderResponse {
  workOrderId: string;
  serviceId: string;
  number: string;
  type: 'INDIVIDUAL' | 'BUSINESS';
  toServe: string;
  address1: string;
  address2?: string | null;
  addressType: 'BUSINESS' | 'HOME';
  phone?: string | null;
  emailLogs?: EmailLogResponse[] | null;
  city: string;
  state: string;
  zipCode: string;
  country?: string | null;
  invoice?: InvoiceResponse | null;
  affidavitTypeId: string;
  affidavitType: AffidavitType;
  serverInstructions?: string | null;
  privateInfo?: PrivateInfo | null;
  documents: WorkOrderDocument[];
  serverId?: string | null;
  server?: UserResponseSchema | null;
  status: ProcessStatus;
  _count?: { comments: number; workOrderAttempts?: number };
  createdById?: string | null;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date | null;
  canceledAt?: Date | null;
  canceledById?: string | null;
  vendor?: WorkOrderVendor;
  workOrderPayments?: WorkOrderPayment[];
  teamId?: string;
  team?: Team;
  user?: UserResponseSchema | null;
  userId?: string | null;
  isPrepaid?: boolean;
  linkedHIWorkOrderID?: string | null;
}

// Work Order Get By ID Response
export interface WorkOrderGetByIdResponse extends WorkOrderResponse {
  service: ServiceResponse;
  workOrderAttempts?: WorkOrderAttempt[] | null;
  vendor?: WorkOrderVendor;
}

// Query and Response Types
export interface WorkOrderGetAllQuery {
  pageNo?: number | string;
  pageSize?: number | string;
  sortField?: 'type' | 'personToServe' | 'city' | 'state' | 'workOrderStatus' | 'createdAt' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  caseId?: string;
  processServiceId?: string;
  search?: string;
}

export type WorkOrderGetAllResponse = WorkOrderResponse[];

export interface WorkOrderDelete {
  message?: string;
}

export type ProcessStatus = 'AWAITING' | 'ASSIGNED' | 'CANCELED' | 'CONFIRMED';
export interface AffidavitType {
  affidavitTypeId: string;
  code: string;
  name: string;
  caseTypeId: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  deletedAt?: string | null;
}

export interface AffidavitTypeGetAllResponse {
  affidavitTypes: AffidavitType[];
}

export interface AffidavitTypeQueryRequest {
  sortField?: 'code' | 'name' | 'createdAt' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  search?: string;
  caseTypeId?: string;
}

export interface WorkOrderCommentResponse {
  commentId: string;
  workOrderId: string;
  comment: string;
  addedById: string;
  addedBy: UserResponseSchema;
  createdAt?: string | null;
}

export interface WorkOrderCommentListResponse {
  comments: WorkOrderCommentResponse[];
  totalRows: number;
  totalPages: number;
  currentPage: number;
}

export interface WorkOrderCommentGetParams {
  pageNo?: number;
  pageSize?: number;
  sortField?: 'createdAt' | 'comment' | 'addedBy';
  sortDirection?: 'asc' | 'desc';
}

export interface WorkOrderCommentCreateRequest {
  comment: string;
}

export type Document = WorkOrderDocument;
export type DocumentPatch = WorkOrderDocumentPatch;
